import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PublicNav from '@/components/PublicNav';
import PublicFooter from '@/components/PublicFooter';
import { useSEO } from '@/hooks/useSEO';
import { usePageTracker } from '@/hooks/usePageTracker';
import { ArrowRight, Brain, Database, Globe, TrendingUp, Shield, Layers, Target, Zap, Plane, Radio, Activity } from 'lucide-react';

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

export default function AboutPage() {
  useSEO({
    title: 'About ForesightRx — Team, Heritage & Vision',
    description: 'Founded by Dr. Amit Dang, powered by MarksMan Healthcare (10+ years, 100+ publications). ForesightRx attends 12+ global conferences annually, meeting biotechs and matching them to pharma BD theses.',
    canonical: 'https://foresightrx.com/about',
  });
  usePageTracker('About');

  const [philRef, philInView] = useInView();
  const [intelRef, intelInView] = useInView();
  const [visionRef, visionInView] = useInView();

  return (
    <div className="min-h-screen bg-[#0A1128] copy-protect">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Person",
        "name": "Dr. Amit Dang",
        "jobTitle": "Founder & Managing Director",
        "worksFor": { "@type": "Organization", "name": "ForesightRx", "url": "https://foresightrx.com" },
        "description": "Over a decade of experience across health economics, outcomes research (HEOR), and biotech business development."
      })}} />
      <PublicNav />

      {/* Header */}
      <section className="pt-32 lg:pt-40 pb-20 lg:pb-28 bg-[#0A1128] relative overflow-hidden" data-testid="about-header">
        <div className="absolute inset-0 grid-bg" />
        <div className="absolute top-1/3 -left-32 w-[400px] h-[400px] bg-[#00709B]/10 rounded-full blur-[120px] pulse-glow" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#70C24C] mb-4 reveal-up">About</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tighter leading-[1.05] reveal-up stagger-1" data-testid="about-heading">
              We built what the
              <br />
              industry has been
              <br />
              <span className="shimmer-text">missing.</span>
            </h1>
            <p className="mt-8 text-lg lg:text-xl text-[#A1A8C3] font-light leading-relaxed max-w-2xl reveal-up stagger-2">
              ForesightRx is the world's first living partnering intelligence platform. We travel the world,
              meet the biotechs, qualify the science at 12+ conferences annually, and feed it into an
              AI-powered matching engine. Not a database. A living, breathing deal machine.
            </p>
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section ref={philRef} className="py-24 lg:py-32 bg-[#111A3A]" data-testid="philosophy">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            <div className={philInView ? 'reveal-up' : 'opacity-0'}>
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#70C24C] mb-4">Our Philosophy</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight mb-8">
                The bio industry is broken.
                <br />
                We are fixing it.
              </h2>
              <div className="space-y-6 text-[#A1A8C3] font-light leading-relaxed">
                <p>
                  $4.2 billion is spent on conference travel every year. Biotechs burn cash on booths
                  and cocktail parties, hoping someone notices their science. Pharma BD teams fly around
                  the world, sit through 40 meetings at BIO, and walk away with 3 leads worth following up.
                </p>
                <p>
                  Meanwhile, everyone pays $50K/year for static databases that were last updated months ago.
                  No context. No qualification. No one is actually connecting the right people.
                </p>
                <p>
                  We decided to build something different. A platform that is continuously alive — fed by
                  real conversations at real conferences, powered by AI matching, and delivered through
                  human-facilitated deal execution. Every conference we attend makes the intelligence
                  better. Every deal we facilitate makes the matching smarter.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {[
                { title: 'Evidence-first approach', desc: 'Every outreach, every deck, every story — it starts with the evidence. What do you have? What do you need? What does the buyer want to see?' },
                { title: 'Speed over perfection', desc: 'We deliver in sprints, not quarters. A good deliverable in 2 weeks is worth more than a perfect one in 3 months.' },
                { title: 'Honesty by default', desc: 'If we do not think we can help, we will say so. If your asset is too early, we will tell you what to do before coming back.' },
                { title: 'Small team, deep focus', desc: 'We work with a limited number of companies at a time. Every engagement gets senior attention from day one.' },
              ].map((item, i) => (
                <div key={i} className={`p-6 rounded-2xl bg-[#0A1128]/60 border border-white/5 hover:border-white/10 transition-all duration-300 ${philInView ? `reveal-up stagger-${i + 1}` : 'opacity-0'}`} data-testid={`philosophy-card-${i}`}>
                  <h4 className="text-sm font-bold text-white mb-2">{item.title}</h4>
                  <p className="text-sm text-[#A1A8C3] font-light leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Intelligence */}
      <section ref={intelRef} className="py-24 lg:py-32 bg-[#0A1128] relative" data-testid="intelligence-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className={`text-center mb-16 ${intelInView ? 'reveal-up' : 'opacity-0'}`}>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#70C24C] mb-4">The Living Engine</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight mb-5">
              Conference-sourced. AI-matched. Human-directed.
            </h2>
            <p className="text-lg text-[#A1A8C3] font-light max-w-2xl mx-auto leading-relaxed">
              Our intelligence is not scraped from press releases. It is sourced from the ground —
              at BIO International, BioChina, BIO-Europe, CPHI, and 12+ conferences annually.
              Every meeting feeds the engine. Every deal makes the matching smarter.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
            {[
              { icon: <Database className="w-5 h-5" />, title: 'Living Pipeline Data', desc: 'Real-time monitoring of global biotech pipelines — updated from every conference we attend.' },
              { icon: <Globe className="w-5 h-5" />, title: 'Deal Intelligence', desc: 'Structured analysis of licensing deals, partnerships, and M&A — with ground-truth context.' },
              { icon: <Brain className="w-5 h-5" />, title: 'AI-Powered Matching', desc: 'Algorithmic identification of high-fit assets and partners. Continuously improving.' },
              { icon: <Activity className="w-5 h-5" />, title: 'Conference Intelligence', desc: 'Every handshake at BIO, BioChina, CPHI feeds our engine. Data that has context no database can replicate.' },
            ].map((item, i) => (
              <div key={i} className={`p-6 rounded-2xl bg-[#111A3A] border border-white/5 hover:border-[#00709B]/20 transition-all duration-300 ${intelInView ? `reveal-up stagger-${i + 1}` : 'opacity-0'}`} data-testid={`intel-card-${i}`}>
                <div className="w-10 h-10 rounded-xl bg-[#00709B]/15 flex items-center justify-center text-[#00709B] mb-4">{item.icon}</div>
                <h4 className="text-sm font-bold text-white mb-2">{item.title}</h4>
                <p className="text-xs text-[#A1A8C3] font-light leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision / Investibility */}
      <section ref={visionRef} className="py-24 lg:py-32 bg-[#111A3A] relative overflow-hidden" data-testid="vision-section">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-[#00709B]/8 to-[#70C24C]/8 blur-[100px]" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className={`text-center mb-16 ${visionInView ? 'reveal-up' : 'opacity-0'}`}>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#70C24C] mb-4">Our Vision</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
              Building the world's most powerful
              <br />
              living matchmaking engine for bio
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
            {[
              { icon: <Layers className="w-5 h-5" />, title: 'Living Intelligence', desc: 'Not a static database. A continuously evolving engine fed by conference intelligence, qualified by humans, powered by AI.' },
              { icon: <Globe className="w-5 h-5" />, title: 'Global Reach', desc: 'Deep networks across China, India, APAC, and global pharma. 12+ conferences a year. Boots on the ground, everywhere.' },
              { icon: <TrendingUp className="w-5 h-5" />, title: 'Compounding Advantage', desc: 'Every conference we attend, every deal we facilitate, every biotech we qualify — the intelligence gets smarter. Competitors can\'t catch up.' },
              { icon: <Shield className="w-5 h-5" />, title: 'Aligned Incentives', desc: 'Biotech: success-based, no retainers. Pharma: subscription intelligence. We win when deals happen.' },
              { icon: <Plane className="w-5 h-5" />, title: 'Trust as Infrastructure', desc: 'Biotechs share confidential data with us — not with pharma. Pharma trusts our judgment because we sit in the room with both sides. Neither side operates the engine.' },
              { icon: <Zap className="w-5 h-5" />, title: 'Built for Action', desc: 'We don\'t stop at reports. We position, introduce, facilitate, and support through to deal close. Outcomes, not access.' },
            ].map((item, i) => (
              <div key={i} className={`p-6 rounded-2xl bg-[#0A1128]/60 border border-white/5 hover:border-white/10 transition-all duration-300 ${visionInView ? `reveal-up stagger-${i + 1}` : 'opacity-0'}`} data-testid={`vision-card-${i}`}>
                <div className="w-10 h-10 rounded-xl bg-[#00709B]/15 flex items-center justify-center text-[#00709B] mb-4">{item.icon}</div>
                <h4 className="text-sm font-bold text-white mb-2">{item.title}</h4>
                <p className="text-sm text-[#A1A8C3] font-light leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32 bg-[#111A3A]" data-testid="about-cta">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl font-extrabold text-white tracking-tight mb-6">
            Let us see if there is a fit
          </h2>
          <p className="text-lg text-[#A1A8C3] font-light mb-10">
            If your asset is at or near the clinic and you need sharper positioning, credible evidence,
            or introductions — we should talk.
          </p>
          <Link
            to="/apply"
            className="inline-flex items-center gap-2 px-10 py-4 rounded-full bg-[#70C24C] text-[#0A1128] text-base font-bold hover:bg-[#5fad3b] hover:-translate-y-0.5 transition-all duration-300 shadow-[0_0_30px_rgba(112,194,76,0.25)]"
            data-testid="about-apply-btn"
          >
            Apply Now <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
